import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import { authApi } from "../../api/auth";
import { startAuthentication } from "@simplewebauthn/browser";
import { formatApiError } from "../../lib/errors";
import ResendVerification from "./ResendVerification";
import PasswordField from "./PasswordField";
import BrandHeader from "../BrandHeader";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [unverified, setUnverified] = useState(false);
  const [noPassword, setNoPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation("auth");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setUnverified(false);
    setNoPassword(false);
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      // 409 is the account we created on someone's behalf: a trial issued from
      // an email capture, a passkey signup, a guest purchase. It has no password
      // to get wrong, so the server's English prose is replaced by a localized
      // line and the one link that actually works for them.
      if (err.response?.status === 409) {
        setNoPassword(true);
        setError(t("login.noPassword"));
      } else if (err.response?.status === 403) {
        // Unverified. The server's prose is English; this is the one line the
        // visitor can read, and `unverified` is what puts the resend button
        // under it.
        setUnverified(true);
        setError(t("login.unverified"));
      } else {
        setError(formatApiError(err, t("login.errorFallback")));
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasskeyLogin = async () => {
    setError("");
    setUnverified(false);
    // `noPassword` too: without it a 409 from an earlier password attempt left
    // its "Set my password" link rendered under a later passkey error.
    setNoPassword(false);
    try {
      const startResp = await authApi.passkeyLoginStart();
      const { challenge_id, options } = startResp.data;
      const assertion = await startAuthentication(options);
      await authApi.passkeyLoginComplete({ challenge_id, credential: assertion });
      window.location.href = "/dashboard";
    } catch (err) {
      // The same 403 the password path handles above, and it used to dead-end
      // right here: the server's English prose with no way out. A passkey
      // signup creates the account unverified, so for those visitors this is
      // the likely failure rather than the rare one — it is the exact wall the
      // 2026-09-06 funnel review found a buyer stuck against.
      if (err.response?.status === 403) {
        setUnverified(true);
        // Passkey login is usernameless, so the Email field above is usually
        // empty and `<ResendVerification>` would render disabled. The server
        // returns the address with this refusal precisely so the button works.
        const known = err.response?.data?.detail?.email;
        if (known) setEmail(known);
        setError(t("login.unverified"));
        return;
      }
      setError(formatApiError(err, err.message || t("login.passkeyErrorFallback")));
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <BrandHeader />
      <div className="flex items-center justify-center min-h-[calc(100vh-6rem)] px-6">
        <div className="w-full max-w-md">
        <div className="feature-card p-8 rounded-lg">
          <h1 className="font-clash text-2xl font-bold text-white mb-6 text-center">{t("login.title")}</h1>
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded mb-4 text-sm">
              <p>{error}</p>
              {unverified && (
                <ResendVerification email={email} className="mt-3" />
              )}
              {noPassword && (
                <Link to="/forgot-password"
                  className="mt-3 inline-block font-semibold text-[#FF5500] hover:text-[#FF7733]">
                  {t("login.noPasswordCta")}
                </Link>
              )}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="font-mono text-xs uppercase tracking-wider text-[#A1A1AA] block mb-2">{t("login.emailLabel")}</label>
              <input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#1a1a1e] border border-white/10 rounded px-4 py-3 text-white focus:border-[#FF5500] focus:outline-none transition-colors" required />
            </div>
            <div>
              <label className="font-mono text-xs uppercase tracking-wider text-[#A1A1AA] block mb-2">{t("login.passwordLabel")}</label>
              <PasswordField value={password} onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password" required />
            </div>
            <div className="text-right">
              <Link to="/forgot-password" className="text-sm text-[#FF5500] hover:text-[#FF7733]">{t("login.forgotPassword")}</Link>
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full py-3 rounded text-sm font-semibold disabled:opacity-50">
              {loading ? t("login.submitting") : t("login.submit")}
            </button>
          </form>
          <div className="my-6 flex items-center gap-4">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-[#A1A1AA]">{t("login.or")}</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>
          <button onClick={handlePasskeyLogin} className="w-full py-3 rounded border border-white/10 text-white text-sm font-medium hover:bg-white/5 transition-colors">
            {t("login.passkeyButton")}
          </button>
          <p className="text-center text-sm text-[#A1A1AA] mt-6">
            {t("login.noAccount")}{" "}
            <Link to="/signup" className="text-[#FF5500] hover:text-[#FF7733]">{t("login.signUp")}</Link>
          </p>
        </div>
        </div>
      </div>
    </div>
  );
}
